import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TASK_PRIORITY } from '../../../core/helpers/constants';
import { createGuid } from '../../../core/helpers/utility';
import { IClient } from '../../../core/models/clients.models';
import { DataFeedService } from '../../api/datafeed.service';
import * as util from '../../../core/helpers/utility'
import { ICampaign } from '../../../core/models/campaigns.model';
import { IUser } from '../../../core/models/auth.models';
import { ITask } from '../../../core/models/tasks.models';
import * as moment from 'moment'
import { TasksApiService } from '../../../pages/tasks/tasks.apiservice';
import { CampaignsApiService } from '../../../pages/campaigns/campaigns.apiservice';
import * as _ from 'underscore'
import { Utility } from '../../utility';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';

type TaskTemplateDueIn =
  | number
  | 'Beginning of Next Month'
  | 'End of This Month'
  | 'Middle of This Month';

type TaskTemplateSource = {
  titleKey: string;
  descriptionKey: string;
  dueInDays: TaskTemplateDueIn;
  priority?: number;
  assignee: { type: 'active' } | { type: 'userSearch'; firstName: string; lastName: string };
};

type TaskTemplate = TaskTemplateSource & {
  title: string;
  description: string;
};

@Component({
  selector: 'modal-add-task',
  templateUrl: './add-task.component.html',
  styleUrls: ['./add-task.component.scss']
})

export class AddTaskModal implements OnInit, OnDestroy {

  calendarRoot: string = 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=';
  calendarDetailsLabel: string = '&details=';
  calendarDatesLabel: string = '&dates=';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Output() oSaveClicked = new EventEmitter<any>();
  @Output() oDeleteClicked = new EventEmitter<any>();
  @Output() oMarkAsCompleteClicked = new EventEmitter<any>();
  @Output() oBulkSaveClicked = new EventEmitter<any>();

  public TASK_PRIORITY = TASK_PRIORITY;

  private readonly taskTemplateSources: TaskTemplateSource[] = [
    {
      titleKey: 'TASK_TEMPLATES.CAMPAIGN_CHECK',
      descriptionKey: 'TASK_TEMPLATES.CAMPAIGN_CHECK_DESC',
      dueInDays: 7,
      assignee: { type: 'active' },
    },
    {
      titleKey: 'TASK_TEMPLATES.CALL_WITH_CLIENT',
      descriptionKey: 'TASK_TEMPLATES.CALL_WITH_CLIENT_DESC',
      dueInDays: 1,
      assignee: { type: 'active' },
    },
    {
      titleKey: 'TASK_TEMPLATES.BUDGET_TO_SUBSCRIPTION',
      descriptionKey: 'TASK_TEMPLATES.BUDGET_TO_SUBSCRIPTION_DESC',
      dueInDays: 'Beginning of Next Month',
      assignee: { type: 'active' },
    },
    {
      titleKey: 'TASK_TEMPLATES.RED_ALERT_QC_CHECK',
      descriptionKey: 'TASK_TEMPLATES.RED_ALERT_QC_CHECK_DESC',
      dueInDays: 2,
      priority: 1,
      assignee: { type: 'userSearch', firstName: 'Eliot', lastName: 'Lee' },
    },
  ];

  sortedTaskTemplates: TaskTemplate[] = [];

  private applyingTemplate = false;
  private pendingTitleTerm = '';

  allClients: Array<IClient> = new Array<IClient>();
  fiteredCampaigns: Array<ICampaign> = new Array<ICampaign>();
  allUsers: Array<IUser> = new Array<IUser>();
  bulkEditTasks: Array<ITask> = [];
  private bulkEditMode = false;
  dropdownOpen = false;
  dropdownExpansion = 0;

  addTaskForm!: FormGroup;
  submitted = false;
  @Input() iCanEdit = true;
  @Input() iCanDelete = true;
  @Input() set iIsBulkEdit(value: boolean) {
    this.bulkEditMode = !!value;
    this.applyModeConfiguration();
  }
  get iIsBulkEdit(): boolean {
    return this.bulkEditMode;
  }
  @Input() set iBulkTasks(value: Array<ITask>) {
    this.bulkEditTasks = value ?? [];
  }
  @Input() set iClientId(value: any) {
    if (value !== null)
      this.addTaskForm.controls['clientId'].patchValue(value);
  }
  @Input() set iCampaignId(value: any) {
    if (value !== null)
      this.addTaskForm.controls['campaignId'].patchValue(value);
  }
  @Input() set iId(value: any) {
    this.id = value;
    if (value !== null) {
      this.taskService.getTaskDetails(value)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          response.data.clientId = response.data.clientId ?? -1;
          response.data.campaignId = response.data.campaignId ?? -1;
          this.addTaskForm.patchValue(response.data);
          this.addTaskForm.patchValue({ dueDate: new Date(response.data.dueDate) })
          this.iCanEdit = this.iCanEdit;
          this.iCanDelete = response.data?.canTaskDelete;
        });
    }
  }
  id: number = -1;

  constructor(
    private dataFeedService: DataFeedService,
    private taskService: TasksApiService,
    public activeModal: NgbActiveModal,
    private campaignService: CampaignsApiService,
    private formBuilder: FormBuilder,
    private translate: TranslateService) {
    this.addTaskForm = this.formBuilder.group({
      id: [null],
      test: [null],
      title: [null, [Validators.required]],
      priority: [2, [Validators.required]],
      campaignId: [null],
      clientId: [null],
      dueDate: [null, Validators.required],
      description: ['', [Validators.required]],
      assignedUserId: ['', [Validators.required]],
      applyAssignedUser: [false],
      applyDueDate: [false],
      isCompleted: [null]
    });

    this.dataFeedService.getAllClients()
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: any) => {
        if (response.good) {
          this.allClients = _.sortBy([...response.data], 'firstName').map(i => { i.fullName = i.firstName + ' ' + i.lastName; return i; });
          // console.log(this.allClients);
          this.addTaskForm.controls['clientId'].patchValue(this.addTaskForm.controls['clientId'].value);
        }
      });

    this.dataFeedService.getAllTaskAssignees()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.allUsers = [...response.data].map((i) => { i.fullName = i.firstName + ' ' + i.lastName; return i; });
          // console.log(this.allUsers);
          if (!this.bulkEditMode && this.f.id.value === null) {
            let dueDate = moment().add(23, 'h').add(59, 'm').add(59, 's');
            this.addTaskForm.patchValue({
              assignedUserId: util.getActiveUser().id,
              dueDate: dueDate.toDate(),
            });
          }

          // If a template was selected before users loaded (e.g., QC Check), re-apply now.
          this.applyTaskTemplateByTitle(this.f.title.value);
        }
      });
    
  }

  get f() { return this.addTaskForm.controls; }

  onDropdownOpen(): void {
    this.dropdownOpen = true;
    this.dropdownExpansion = 0;

    setTimeout(() => {
      const modalBody = document.querySelector('.add-task-modal__body');
      const dropdown = Array.from(document.querySelectorAll('.ng-dropdown-panel'))
        .find(panel => panel.getBoundingClientRect().height > 0);
      if (!modalBody || !dropdown) {
        return;
      }

      const modalBodyBottom = modalBody.getBoundingClientRect().bottom;
      const dropdownBottom = dropdown.getBoundingClientRect().bottom;
      this.dropdownExpansion = Math.max(0, Math.ceil(dropdownBottom - modalBodyBottom + 16));
    });
  }

  onDropdownClose(): void {
    this.dropdownOpen = false;
    this.dropdownExpansion = 0;
  }

  private localizeTaskTemplate(template: TaskTemplateSource): TaskTemplate {
    return {
      ...template,
      title: this.translate.instant(template.titleKey),
      description: this.translate.instant(template.descriptionKey),
    };
  }

  private refreshTaskTemplates(): void {
    this.sortedTaskTemplates = this.taskTemplateSources
      .map(template => this.localizeTaskTemplate(template))
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }

  private getTaskTemplateByTitle(title: string): TaskTemplate | undefined {
    return this.sortedTaskTemplates.find(t => t.title === title);
  }

  updateData(data: IClient) {
    this.addTaskForm.patchValue(data);
  }

  ngOnInit() {
    this.refreshTaskTemplates();
    this.applyModeConfiguration();

    this.f.applyAssignedUser.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled: boolean) => {
        if (!this.bulkEditMode) return;
        if (enabled) {
          this.f.assignedUserId.enable({ emitEvent: false });
        } else {
          this.f.assignedUserId.reset(null, { emitEvent: false });
          this.f.assignedUserId.disable({ emitEvent: false });
        }
      });

    this.f.applyDueDate.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled: boolean) => {
        if (!this.bulkEditMode) return;
        if (enabled) {
          this.f.dueDate.enable({ emitEvent: false });
        } else {
          this.f.dueDate.reset(null, { emitEvent: false });
          this.f.dueDate.disable({ emitEvent: false });
        }
      });

    this.f.title.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((title: string) => {
        // Clear any pending typed term once a value is set.
        this.pendingTitleTerm = '';
        this.applyTaskTemplateByTitle(title);
      });

    this.f.clientId.valueChanges.subscribe(value => {
      if (value == null || value === -1) {
        this.fiteredCampaigns = [];
      } else {
        this.dataFeedService.getAllClientCampaigns([value])
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response: any) => {
            if (response.good) {
              this.fiteredCampaigns = _.sortBy([...response.data], 'name');
              if (!this.fiteredCampaigns.find(x => x.id === this.addTaskForm.controls["campaignId"].value)) {
                this.addTaskForm.patchValue({ campaignId: null });
              }
            }
          });
      }
    });

    this.f.clientId.valueChanges
      .subscribe(val => {
        if (!!val && val === -1) {
          this.fiteredCampaigns = [];
        }
      });
  }

  onTitleSearch(evt: any): void {
    // ng-select emits either a string term or an object like { term: string, items: any[] }
    const term = (typeof evt === 'string') ? evt : (evt?.term ?? '');
    this.pendingTitleTerm = (term ?? '').toString();
  }

  commitTypedTitle(): void {
    const current = this.f.title.value;
    const typed = (this.pendingTitleTerm ?? '').trim();
    if ((current === null || current === undefined || current === '') && typed) {
      this.addTaskForm.patchValue({ title: typed });
    }
  }

  private isAddMode(): boolean {
    // In this codebase, "Add" is typically represented by id === null.
    // id starts at -1 before inputs are set.
    return this.id === null || this.id === -1;
  }

  private findUserIdByName(firstName: string, lastName: string): string | null {
    const match = this.allUsers.find(u =>
      (u.firstName ?? '').toLowerCase() === firstName.toLowerCase() &&
      (u.lastName ?? '').toLowerCase() === lastName.toLowerCase()
    );
    return match?.id ?? null;
  }

  private applyTaskTemplateByTitle(title: string | null | undefined): void {
    if (!this.isAddMode()) return;
    if (!title) return;
    if (this.applyingTemplate) return;

    const template = this.getTaskTemplateByTitle(title);
    if (!template) return;

    this.applyingTemplate = true;
    try {
      const dueDate = this.calculateDueDateFromTemplate(template.dueInDays);

      const patch: any = {
        description: template.description,
        dueDate,
      };

      if (typeof template.priority === 'number') {
        patch.priority = template.priority;
      }

      if (template.assignee.type === 'active') {
        patch.assignedUserId = util.getActiveUser().id;
      } else {
        const userId = this.findUserIdByName(template.assignee.firstName, template.assignee.lastName);
        if (userId) {
          patch.assignedUserId = userId;
        }
      }

      this.addTaskForm.patchValue(patch);
    } finally {
      this.applyingTemplate = false;
    }
  }

  private calculateDueDateFromTemplate(dueIn: TaskTemplateDueIn): Date {
    const endOfDay = (m: moment.Moment) => m.endOf('day').toDate();

    if (typeof dueIn === 'number') {
      return endOfDay(moment().add(dueIn, 'days'));
    }

    const moveToMondayIfWeekend = (m: moment.Moment) => {
      const day = m.isoWeekday(); // 1=Mon ... 6=Sat 7=Sun
      if (day === 6) return m.add(2, 'days');
      if (day === 7) return m.add(1, 'days');
      return m;
    };

    const moveToFridayIfWeekend = (m: moment.Moment) => {
      const day = m.isoWeekday();
      if (day === 6) return m.subtract(1, 'days');
      if (day === 7) return m.subtract(2, 'days');
      return m;
    };

    switch (dueIn) {
      case 'Beginning of Next Month': {
        const m = moveToMondayIfWeekend(moment().add(1, 'month').startOf('month'));
        return endOfDay(m);
      }
      case 'End of This Month': {
        const m = moveToFridayIfWeekend(moment().endOf('month'));
        return endOfDay(m);
      }
      case 'Middle of This Month': {
        const m = moment().date(15);
        const day = m.isoWeekday();
        // Closest weekday to the 15th: Sat -> Fri (14th), Sun -> Mon (16th)
        if (day === 6) m.subtract(1, 'days');
        else if (day === 7) m.add(1, 'days');
        return endOfDay(m);
      }
      default:
        return endOfDay(moment());
    }
  }

  deleteTask() {
    Swal.fire({
      title: this.translate.instant('ARE_YOU_SURE'),
      text: this.translate.instant('CANNOT_REVERT_THIS_ACTION'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('YES_DELETE_IT')
    }).then((result) => {
      if (result.isConfirmed) { 
        this.oDeleteClicked.emit(this.f.id.value);
      }
    });
  }

  markAsComplete() {
    this.oMarkAsCompleteClicked.emit({ id: this.f.id.value, isCompleted: true, completedOn: moment().utc().toDate() });
  }

  onSubmit() {
    this.submitted = true;
    if (this.bulkEditMode) {
      const patch: any = {};
      if (this.f.applyAssignedUser.value) {
        if (!this.f.assignedUserId.value) {
          return;
        }
        patch.assignedUserId = this.f.assignedUserId.value;
      }
      if (this.f.applyDueDate.value) {
        if (!this.f.dueDate.value) {
          return;
        }
        patch.dueDate = Utility.getTaskDueDateFromDateTime(this.f.dueDate.value);
      }

      if (Object.keys(patch).length === 0) {
        return;
      }

      this.oBulkSaveClicked.emit({ tasks: this.bulkEditTasks, patch });
      return;
    }

    if (this.addTaskForm.invalid)
      return;
    if (this.f.id.value === null || this.f.id.value === -1)
      this.f.id.setValue(createGuid())

    if (this.f.campaignId.value === -1)
      this.f.campaignId.setValue(null);
    if (this.f.clientId.value === -1)
      this.f.clientId.setValue(null);

    let formValues: ITask = this.addTaskForm.value;

    formValues = this.trimObjectStrings(formValues);

    formValues.dueDate = Utility.getTaskDueDateFromDateTime(formValues.dueDate);
    this.oSaveClicked.emit(formValues);
  }

  private applyModeConfiguration() {
    if (!this.addTaskForm) return;

    if (this.bulkEditMode) {
      this.clearValidators(['title', 'priority', 'campaignId', 'clientId', 'dueDate', 'description', 'assignedUserId']);
      this.f.applyAssignedUser.setValue(false, { emitEvent: false });
      this.f.applyDueDate.setValue(false, { emitEvent: false });
      this.f.assignedUserId.disable({ emitEvent: false });
      this.f.dueDate.disable({ emitEvent: false });
      this.f.assignedUserId.setValue(null, { emitEvent: false });
      this.f.dueDate.setValue(null, { emitEvent: false });
      this.f.title.setValue(null, { emitEvent: false });
      this.f.priority.setValue(2, { emitEvent: false });
      this.f.campaignId.setValue(null, { emitEvent: false });
      this.f.clientId.setValue(null, { emitEvent: false });
      this.f.description.setValue('', { emitEvent: false });
      this.f.isCompleted.setValue(null, { emitEvent: false });
      return;
    }

    this.setValidators('title', [Validators.required]);
    this.setValidators('priority', [Validators.required]);
    this.setValidators('dueDate', [Validators.required]);
    this.setValidators('description', [Validators.required]);
    this.setValidators('assignedUserId', [Validators.required]);
    this.f.assignedUserId.enable({ emitEvent: false });
    this.f.dueDate.enable({ emitEvent: false });
  }

  private setValidators(controlName: string, validators: any[]) {
    const control = this.addTaskForm?.get(controlName);
    control?.setValidators(validators);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private clearValidators(controlNames: string[]) {
    controlNames.forEach(name => {
      const control = this.addTaskForm?.get(name);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

}
